import PropTypes from "prop-types"
import { useCallback, useState } from "react"
import { useDispatch } from "react-redux"

import { setWarranty } from "areas/cart/store/actions"
import Checkbox from "components/ui/checkbox"
import Expand from "components/ui/expand"
import Image from "components/ui/image"
import { jewlr_t, isTestMode } from "helpers/application"
import { cn } from "styles/utils"

import WarrantyPlans from "./warranty-plans"

const WarrantyAddon = ({ item, source }) => {
  const dispatch = useDispatch()
  const warrantyPlans = item.warranty_plans || []
  const selectedWarrantyPlan = (item.addons || []).find(
    (addon) => addon.addon_type === "WARRANTY"
  )
  const [showWarranty, setShowWarranty] = useState(!!selectedWarrantyPlan)

  const selectWarrantyOption = useCallback(
    (plan) => {
      if (item) {
        if (!isTestMode) {
          if (plan) {
            dataLayer.push({
              event: "added-warranty-to-cart",
              source,
              warrantyPlan: plan.id,
            })
          } else {
            dataLayer.push({
              event: "removed-warranty-from-cart",
              source,
            })
          }
        }
        dispatch(setWarranty(item.id, plan))
      }
    },
    [dispatch, item]
  )

  const handleAddWarrantyPlan = useCallback(() => {
    setShowWarranty(!showWarranty)
    if (!showWarranty) {
      if (!isTestMode) {
        dataLayer.push({ event: "expand-cart-line-item-warranty-option" })
      }

      const bestSellerPlan = (warrantyPlans || []).find(
        (plan) => plan.label === "Best Seller"
      )
      selectWarrantyOption(bestSellerPlan || warrantyPlans?.[0])
    } else {
      selectWarrantyOption(null)
    }
  }, [showWarranty, selectWarrantyOption, warrantyPlans])

  if (!item.warranty_plans?.length) {
    return null
  }

  return (
    <section
      aria-describedby={`cart-line-item-warranty-addon-description-${item.id}`}
      aria-label="Protection Plan Options"
      className={cn(
        source === "atc-modal" ? "bg-grey-100 mb-12 px-16 py-12" : "px-12 py-14"
      )}
    >
      <Checkbox
        aria-checked={showWarranty}
        aria-controls={`cart-line-item-warranty-addon-content-${item.id}`}
        checkboxClassName="rounded-full border-primary text-white aria-checked:bg-primary"
        checked={showWarranty}
        className="w-full flex-wrap items-center gap-8"
        data-cy={`cart-line-item-protection-checkbox-${item.style_code}`}
        id={`cart-line-item-warranty-addon-heading-${item.id}`}
        onChange={handleAddWarrantyPlan}
        role="switch"
      >
        <div className="flex flex-1 items-center justify-between gap-8">
          <h3 className="text-16 font-semibold">Add a Protection Plan</h3>
          <span className="text-grey-65 text-12 grow-0 text-right">
            Powered by
            <Image
              alt="Extend"
              className="ml-4 inline-block h-12 w-auto align-baseline"
              height="16"
              src={require("images/checkout/extend.svg")}
              width="64"
            />
          </span>
        </div>
        <span
          className={cn(
            "text-14 basis-full font-medium",
            source === "atc-modal" && "sr-only"
          )}
          id={`cart-line-item-warranty-addon-description-${item.id}`}
        >
          Select a plan to protect your {jewlr_t("terms.jewelry")} from
          accidental damage.
        </span>
      </Checkbox>
      <Expand
        aria-labelledby={`cart-line-item-warranty-addon-heading-${item.id}`}
        className={cn(
          source === "atc-modal" ? "pt-12" : "bg-grey-100 mt-16 p-16"
        )}
        id={`cart-line-item-warranty-addon-content-${item.id}`}
        role="region"
        show={showWarranty}
      >
        <WarrantyPlans
          item={item}
          selectWarrantyOption={selectWarrantyOption}
          selectedWarrantyPlan={selectedWarrantyPlan}
        />
      </Expand>
    </section>
  )
}

WarrantyAddon.propTypes = {
  item: PropTypes.object,
  source: PropTypes.string.isRequired,
}

export default WarrantyAddon
